import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Search, Eye, CheckCircle, XCircle, Clock,
  DollarSign, MousePointer, TrendingUp, Loader2,
  Globe, Mail, ExternalLink, ChevronLeft, ChevronRight,
  CreditCard, Send, History, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import Layout from '../components/Layout';
import api from '../utils/api';
import { toast } from 'sonner';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  approved: 'bg-green-100 text-green-800 border-green-300',
  rejected: 'bg-red-100 text-red-800 border-red-300',
  suspended: 'bg-gray-100 text-gray-800 border-gray-300'
};

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  suspended: XCircle
};

export default function AffiliateManagement() {
  const [activeTab, setActiveTab] = useState('pending');
  const [affiliates, setAffiliates] = useState([]);
  const [pendingList, setPendingList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPending, setLoadingPending] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Modal states
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [affiliateDetail, setAffiliateDetail] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Payout states
  const [pendingPayouts, setPendingPayouts] = useState([]);
  const [payoutHistory, setPayoutHistory] = useState([]);
  const [loadingPayouts, setLoadingPayouts] = useState(false);
  const [payoutMethod, setPayoutMethod] = useState('paypal');
  const [payoutReference, setPayoutReference] = useState('');
  const [payoutNotes, setPayoutNotes] = useState('');
  const [minimumPayout, setMinimumPayout] = useState(50);

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/affiliates/stats');
      setStats(res.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await api.get('/admin/affiliates/pending');
      setPendingList(res.data.pending || []);
    } catch (error) {
      console.error('Failed to load pending:', error);
    } finally {
      setLoadingPending(false);
    }
  }, []);

  const loadAffiliates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (statusFilter) params.append('status', statusFilter);
      const res = await api.get(`/admin/affiliates?${params.toString()}`);
      setAffiliates(res.data.affiliates || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (error) {
      console.error('Failed to load affiliates:', error);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  const loadPendingPayouts = useCallback(async () => {
    setLoadingPayouts(true);
    try {
      const res = await api.get('/admin/affiliates/payouts/pending');
      setPendingPayouts(res.data.affiliates || []);
      if (res.data.minimum_payout) setMinimumPayout(res.data.minimum_payout);
    } catch (error) {
      console.error('Failed to load pending payouts:', error);
    } finally {
      setLoadingPayouts(false);
    }
  }, []);

  const loadPayoutHistory = useCallback(async () => {
    try {
      const res = await api.get('/admin/affiliates/payouts/history');
      setPayoutHistory(res.data.payouts || []);
    } catch (error) {
      console.error('Failed to load payout history:', error);
    }
  }, []);

  useEffect(() => {
    loadStats();
    loadPending();
    loadPendingPayouts();
    loadPayoutHistory();
  }, [loadStats, loadPending, loadPendingPayouts, loadPayoutHistory]);

  useEffect(() => {
    loadAffiliates();
  }, [loadAffiliates]);

  const handleViewDetail = async (affiliate) => {
    setSelectedAffiliate(affiliate);
    try {
      const res = await api.get(`/admin/affiliates/${affiliate.affiliate_id}`);
      setAffiliateDetail(res.data);
    } catch (error) {
      console.error('Failed to load detail:', error);
      setAffiliateDetail(null);
    }
    setShowDetailModal(true);
  };

  const handleApprove = async (affiliate) => {
    setActionLoading(true);
    try {
      await api.put(`/admin/affiliates/${affiliate.affiliate_id}/approve`);
      toast.success(`Approved ${affiliate.name} as an affiliate partner`);
      loadPending();
      loadAffiliates();
      loadStats();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve affiliate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAffiliate) return;
    setActionLoading(true);
    try {
      await api.put(`/admin/affiliates/${selectedAffiliate.affiliate_id}/reject`, {
        approved: false,
        rejection_reason: rejectionReason
      });
      toast.success(`Rejected ${selectedAffiliate.name}'s application`);
      setShowRejectModal(false);
      setRejectionReason('');
      loadPending();
      loadAffiliates();
      loadStats();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject affiliate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSuspend = async (affiliate) => {
    setActionLoading(true);
    try {
      await api.put(`/admin/affiliates/${affiliate.affiliate_id}/suspend`);
      toast.success(`Suspended ${affiliate.name}`);
      loadAffiliates();
      loadStats();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to suspend affiliate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstate = async (affiliate) => {
    setActionLoading(true);
    try {
      await api.put(`/admin/affiliates/${affiliate.affiliate_id}/reinstate`);
      toast.success(`Reinstated ${affiliate.name}`);
      loadAffiliates();
      loadStats();
      setShowDetailModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reinstate affiliate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleProcessPayout = async (affiliate) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/admin/affiliates/${affiliate.affiliate_id}/payout`, {
        method: payoutMethod,
        reference: payoutReference || null,
        notes: payoutNotes || null
      });
      toast.success(res.data.message);
      setShowPayoutModal(false);
      setPayoutMethod('paypal');
      setPayoutReference('');
      setPayoutNotes('');
      setSelectedAffiliate(null);
      loadPendingPayouts();
      loadPayoutHistory();
      loadStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payout');
    } finally {
      setActionLoading(false);
    }
  };

  const filteredAffiliates = searchQuery
    ? affiliates.filter(a =>
        a.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.referral_code?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : affiliates;

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="affiliate-management">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="affiliate-management-title">Affiliate Program</h1>
          <p className="text-gray-600">Manage affiliate partners and track program performance</p>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="affiliate-stats">
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg"><Users className="w-5 h-5 text-blue-600" /></div>
                  <div>
                    <p className="text-sm text-gray-500">Total Affiliates</p>
                    <p className="text-2xl font-bold">{stats.overview.total_affiliates}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-lg"><Clock className="w-5 h-5 text-yellow-600" /></div>
                  <div>
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-bold">{stats.overview.pending_applications}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg"><MousePointer className="w-5 h-5 text-green-600" /></div>
                  <div>
                    <p className="text-sm text-gray-500">Total Clicks</p>
                    <p className="text-2xl font-bold">{stats.overview.total_clicks.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-5">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg"><DollarSign className="w-5 h-5 text-purple-600" /></div>
                  <div>
                    <p className="text-sm text-gray-500">Pending Payouts</p>
                    <p className="text-2xl font-bold">${stats.overview.pending_payouts?.toFixed(2) || '0.00'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending Applications {pendingList.length > 0 && (
                <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs rounded-full">
                  {pendingList.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All Affiliates</TabsTrigger>
            <TabsTrigger value="payouts" data-testid="tab-payouts">
              Payouts {pendingPayouts.length > 0 && (
                <Badge className="ml-2 bg-green-600 h-5 w-5 p-0 flex items-center justify-center text-xs rounded-full">
                  {pendingPayouts.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="top" data-testid="tab-top">Top Performers</TabsTrigger>
          </TabsList>

          {/* Pending Applications Tab */}
          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle>Pending Applications</CardTitle>
                <CardDescription>Review and approve or reject affiliate applications</CardDescription>
              </CardHeader>
              <CardContent>
                {loadingPending ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : pendingList.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                    <p className="font-medium">No pending applications</p>
                    <p className="text-sm">All caught up!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingList.map((affiliate) => (
                      <div
                        key={affiliate.affiliate_id}
                        className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
                        data-testid={`pending-affiliate-${affiliate.affiliate_id}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-semibold text-gray-900">{affiliate.name}</p>
                            <Badge variant="outline" className={statusColors.pending}>{affiliate.status}</Badge>
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{affiliate.email}</span>
                            {affiliate.website_url && (
                              <span className="flex items-center gap-1"><Globe className="w-3 h-3" />{affiliate.website_url}</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-1">
                            <span className="font-medium">Promotion plan:</span> {affiliate.promotion_method}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleViewDetail(affiliate)}
                            data-testid={`view-affiliate-${affiliate.affiliate_id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(affiliate)}
                            disabled={actionLoading}
                            data-testid={`approve-affiliate-${affiliate.affiliate_id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => { setSelectedAffiliate(affiliate); setShowRejectModal(true); }}
                            disabled={actionLoading}
                            data-testid={`reject-affiliate-${affiliate.affiliate_id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Affiliates Tab */}
          <TabsContent value="all">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>All Affiliates</CardTitle>
                    <CardDescription>Browse and manage all affiliate partners</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search affiliates..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 w-64"
                        data-testid="affiliate-search"
                      />
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                      className="h-10 rounded-md border border-gray-200 px-3 text-sm"
                      data-testid="affiliate-status-filter"
                    >
                      <option value="">All Statuses</option>
                      <option value="approved">Approved</option>
                      <option value="pending">Pending</option>
                      <option value="rejected">Rejected</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                  </div>
                ) : filteredAffiliates.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No affiliates found</p>
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="affiliates-table">
                        <thead>
                          <tr className="border-b text-left text-sm text-gray-500">
                            <th className="pb-3 font-medium">Name</th>
                            <th className="pb-3 font-medium">Status</th>
                            <th className="pb-3 font-medium">Referral Code</th>
                            <th className="pb-3 font-medium">Clicks</th>
                            <th className="pb-3 font-medium">Conversions</th>
                            <th className="pb-3 font-medium">Earnings</th>
                            <th className="pb-3 font-medium">Applied</th>
                            <th className="pb-3 font-medium"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredAffiliates.map((affiliate) => {
                            const StatusIcon = statusIcons[affiliate.status] || Clock;
                            return (
                              <tr key={affiliate.affiliate_id} className="border-b last:border-0 hover:bg-gray-50">
                                <td className="py-3">
                                  <p className="font-medium text-gray-900">{affiliate.name}</p>
                                  <p className="text-sm text-gray-500">{affiliate.email}</p>
                                </td>
                                <td className="py-3">
                                  <Badge variant="outline" className={statusColors[affiliate.status] || ''}>
                                    <StatusIcon className="w-3 h-3 mr-1" />
                                    {affiliate.status}
                                  </Badge>
                                </td>
                                <td className="py-3">
                                  <code className="text-sm bg-gray-100 px-2 py-1 rounded">{affiliate.referral_code}</code>
                                </td>
                                <td className="py-3 font-medium">{affiliate.total_clicks?.toLocaleString() || 0}</td>
                                <td className="py-3 font-medium">{affiliate.total_conversions || 0}</td>
                                <td className="py-3 font-medium text-green-600">${(affiliate.total_earnings || 0).toFixed(2)}</td>
                                <td className="py-3 text-sm text-gray-500">
                                  {affiliate.created_at ? new Date(affiliate.created_at).toLocaleDateString() : '-'}
                                </td>
                                <td className="py-3">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleViewDetail(affiliate)}
                                    data-testid={`view-detail-${affiliate.affiliate_id}`}
                                  >
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                            <ChevronLeft className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                            <ChevronRight className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payouts Tab */}
          <TabsContent value="payouts">
            <div className="space-y-6">
              {/* Pending Payouts */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        <Banknote className="w-5 h-5" />
                        Pending Payouts
                      </CardTitle>
                      <CardDescription>Affiliates with earnings ready to be paid out</CardDescription>
                    </div>
                    <Badge variant="outline" className="text-sm px-3 py-1 border-amber-300 text-amber-700 bg-amber-50">
                      Minimum payout: ${minimumPayout.toFixed(2)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {loadingPayouts ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                    </div>
                  ) : pendingPayouts.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <CheckCircle className="w-12 h-12 mx-auto mb-3 text-green-300" />
                      <p className="font-medium">No pending payouts</p>
                      <p className="text-sm">All affiliates have been paid!</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pendingPayouts.map((affiliate) => {
                        const isEligible = affiliate.eligible_for_payout !== false && (affiliate.pending_earnings || 0) >= minimumPayout;
                        return (
                        <div
                          key={affiliate.affiliate_id}
                          className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${isEligible ? 'hover:bg-gray-50' : 'bg-gray-50/50 opacity-75'}`}
                          data-testid={`payout-affiliate-${affiliate.affiliate_id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-1">
                              <p className="font-semibold text-gray-900">{affiliate.name}</p>
                              <code className="text-xs bg-gray-100 px-2 py-0.5 rounded">{affiliate.referral_code}</code>
                              {!isEligible && (
                                <Badge variant="outline" className="text-xs border-amber-300 text-amber-600 bg-amber-50">
                                  Below ${minimumPayout} minimum
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{affiliate.email}</span>
                              {affiliate.paypal_email && (
                                <span className="flex items-center gap-1"><CreditCard className="w-3 h-3" />PayPal: {affiliate.paypal_email}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right mr-2">
                              <p className={`text-2xl font-bold ${isEligible ? 'text-green-600' : 'text-amber-500'}`}>${(affiliate.pending_earnings || 0).toFixed(2)}</p>
                              <p className="text-xs text-gray-500">Total earned: ${(affiliate.total_earnings || 0).toFixed(2)}</p>
                            </div>
                            <Button
                              className="bg-green-600 hover:bg-green-700"
                              disabled={!isEligible}
                              onClick={() => {
                                setSelectedAffiliate(affiliate);
                                setPayoutMethod(affiliate.paypal_email ? 'paypal' : 'bank_transfer');
                                setShowPayoutModal(true);
                              }}
                              data-testid={`process-payout-${affiliate.affiliate_id}`}
                            >
                              <Send className="w-4 h-4 mr-2" /> {isEligible ? 'Process Payout' : 'Below Minimum'}
                            </Button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Payout History */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="w-5 h-5" />
                    Payout History
                  </CardTitle>
                  <CardDescription>Record of all processed payouts</CardDescription>
                </CardHeader>
                <CardContent>
                  {payoutHistory.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <History className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                      <p>No payouts processed yet</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full" data-testid="payout-history-table">
                        <thead>
                          <tr className="border-b text-left text-sm text-gray-500">
                            <th className="pb-3 font-medium">Affiliate</th>
                            <th className="pb-3 font-medium">Amount</th>
                            <th className="pb-3 font-medium">Commissions</th>
                            <th className="pb-3 font-medium">Method</th>
                            <th className="pb-3 font-medium">Reference</th>
                            <th className="pb-3 font-medium">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payoutHistory.map((payout) => (
                            <tr key={payout.payout_id} className="border-b last:border-0 hover:bg-gray-50">
                              <td className="py-3">
                                <p className="font-medium">{payout.affiliate_name}</p>
                                <p className="text-sm text-gray-500">{payout.affiliate_email}</p>
                              </td>
                              <td className="py-3 font-bold text-green-600">${(payout.amount || 0).toFixed(2)}</td>
                              <td className="py-3">{payout.commission_count}</td>
                              <td className="py-3">
                                <Badge variant="outline" className="capitalize">{payout.method?.replace('_', ' ')}</Badge>
                              </td>
                              <td className="py-3 text-sm text-gray-600">{payout.reference || '-'}</td>
                              <td className="py-3 text-sm text-gray-500">
                                {payout.created_at ? new Date(payout.created_at).toLocaleDateString() : '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Top Performers Tab */}
          <TabsContent value="top">
            <Card>
              <CardHeader>
                <CardTitle>Top Performing Affiliates</CardTitle>
                <CardDescription>Highest earning affiliate partners</CardDescription>
              </CardHeader>
              <CardContent>
                {!stats?.top_affiliates?.length ? (
                  <div className="text-center py-12 text-gray-500">
                    <TrendingUp className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No affiliate data yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.top_affiliates.map((affiliate, index) => (
                      <div
                        key={affiliate.referral_code}
                        className="flex items-center justify-between p-4 rounded-lg border hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                            index === 1 ? 'bg-gray-100 text-gray-700' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-50 text-gray-500'
                          }`}>
                            {index + 1}
                          </div>
                          <div>
                            <p className="font-semibold">{affiliate.name}</p>
                            <p className="text-sm text-gray-500">Code: {affiliate.referral_code}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-center">
                            <p className="text-gray-500">Conversions</p>
                            <p className="font-bold">{affiliate.total_conversions || 0}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-gray-500">Earnings</p>
                            <p className="font-bold text-green-600">${(affiliate.total_earnings || 0).toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Affiliate Details</DialogTitle>
              <DialogDescription>Review affiliate application and performance</DialogDescription>
            </DialogHeader>
            {selectedAffiliate && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{selectedAffiliate.name}</h3>
                    <p className="text-sm text-gray-500">{selectedAffiliate.email}</p>
                  </div>
                  <Badge variant="outline" className={statusColors[selectedAffiliate.status] || ''}>
                    {selectedAffiliate.status}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  {selectedAffiliate.website_url && (
                    <div>
                      <p className="text-gray-500">Website</p>
                      <a href={selectedAffiliate.website_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        {selectedAffiliate.website_url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                  {selectedAffiliate.portfolio_url && (
                    <div>
                      <p className="text-gray-500">Portfolio</p>
                      <a href={selectedAffiliate.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        {selectedAffiliate.portfolio_url} <ExternalLink className="w-3 h-3" />
                      </a>
                    </div>
                  )}
                  <div>
                    <p className="text-gray-500">Referral Code</p>
                    <code className="bg-gray-100 px-2 py-1 rounded">{selectedAffiliate.referral_code}</code>
                  </div>
                  <div>
                    <p className="text-gray-500">Applied</p>
                    <p>{selectedAffiliate.created_at ? new Date(selectedAffiliate.created_at).toLocaleDateString() : '-'}</p>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-500 mb-1">Promotion Method</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">{selectedAffiliate.promotion_method}</p>
                </div>

                {selectedAffiliate.status === 'approved' && (
                  <div className="grid grid-cols-3 gap-3">
                    <Card className="bg-blue-50 border-blue-200">
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-blue-600">Clicks</p>
                        <p className="text-xl font-bold text-blue-700">{selectedAffiliate.total_clicks || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-green-50 border-green-200">
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-green-600">Conversions</p>
                        <p className="text-xl font-bold text-green-700">{selectedAffiliate.total_conversions || 0}</p>
                      </CardContent>
                    </Card>
                    <Card className="bg-purple-50 border-purple-200">
                      <CardContent className="pt-4 text-center">
                        <p className="text-xs text-purple-600">Earnings</p>
                        <p className="text-xl font-bold text-purple-700">${(selectedAffiliate.total_earnings || 0).toFixed(2)}</p>
                      </CardContent>
                    </Card>
                  </div>
                )}

                <DialogFooter className="gap-2">
                  {selectedAffiliate.status === 'pending' && (
                    <>
                      <Button
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleApprove(selectedAffiliate)}
                        disabled={actionLoading}
                        data-testid="modal-approve-btn"
                      >
                        {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle className="w-4 h-4 mr-1" />}
                        Approve
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => { setShowRejectModal(true); }}
                        disabled={actionLoading}
                        data-testid="modal-reject-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </>
                  )}
                  {selectedAffiliate.status === 'approved' && (
                    <Button
                      variant="destructive"
                      onClick={() => handleSuspend(selectedAffiliate)}
                      disabled={actionLoading}
                      data-testid="modal-suspend-btn"
                    >
                      Suspend Affiliate
                    </Button>
                  )}
                  {selectedAffiliate.status === 'suspended' && (
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      onClick={() => handleReinstate(selectedAffiliate)}
                      disabled={actionLoading}
                      data-testid="modal-reinstate-btn"
                    >
                      Reinstate Affiliate
                    </Button>
                  )}
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Reject Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Application</DialogTitle>
              <DialogDescription>
                Provide a reason for rejecting {selectedAffiliate?.name}'s application.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Textarea
                placeholder="Reason for rejection (optional)..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="min-h-[80px]"
                data-testid="rejection-reason-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleReject}
                disabled={actionLoading}
                data-testid="confirm-reject-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Reject Application
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Payout Modal */}
        <Dialog open={showPayoutModal} onOpenChange={setShowPayoutModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Process Payout</DialogTitle>
              <DialogDescription>
                Send payout of <strong className="text-green-600">${(selectedAffiliate?.pending_earnings || 0).toFixed(2)}</strong> to {selectedAffiliate?.name}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Payment Method</label>
                <Select value={payoutMethod} onValueChange={setPayoutMethod}>
                  <SelectTrigger data-testid="payout-method-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paypal">PayPal</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="crypto">Cryptocurrency</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {selectedAffiliate?.paypal_email && payoutMethod === 'paypal' && (
                  <p className="text-xs text-gray-500 mt-1">PayPal: {selectedAffiliate.paypal_email}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Transaction Reference</label>
                <Input
                  placeholder="e.g., PayPal Transaction ID, Wire reference..."
                  value={payoutReference}
                  onChange={(e) => setPayoutReference(e.target.value)}
                  data-testid="payout-reference-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1.5 block">Notes (optional)</label>
                <Textarea
                  placeholder="Internal notes about this payout..."
                  value={payoutNotes}
                  onChange={(e) => setPayoutNotes(e.target.value)}
                  className="min-h-[60px]"
                  data-testid="payout-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowPayoutModal(false)}>Cancel</Button>
              <Button
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleProcessPayout(selectedAffiliate)}
                disabled={actionLoading}
                data-testid="confirm-payout-btn"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Send ${(selectedAffiliate?.pending_earnings || 0).toFixed(2)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
